import { Checkbox } from '@mantine/core';
import React, { useState, useCallback } from 'react';
import { ReactComponent as ChevronDown } from '@assets/svg/chevron-down.svg';
import { ReactComponent as ArrowUpImg } from '@assets/svg/arrow-up.svg';
import { Tag } from '@components/Tag/Tag';
import { noop } from '@utils/noop';
import { pick } from '@utils/pick';
import {
  LoadMoreContext,
  LoadMoreCtx,
  useVirtualPages,
} from '@components/LoadMoreComponent/LoadMoreContext';
import { LoadMoreComponent } from '@components/LoadMoreComponent/LoadMoreComponent';
import { FilterWithOperation, ITagCategoryProps } from './types';
import { TagCheckbox } from './TagCheckbox';
import CheckIcon from './CheckIcon';
import { matchesTags } from './utils';

export function TagCategory(
  props: ITagCategoryProps,
): React.ReactElement<ITagCategoryProps> {
  const { category, selectedMap, searchText, onTagFilterChange, ...rest } =
    props;
  const tags = category.tags ?? [];
  const categorySelected = selectedMap.get(category.id);
  const hasCategory = categorySelected?.size === category.tags?.length;
  const hasTagMatches = matchesTags(category, searchText);
  const openByDefault = !!categorySelected || hasTagMatches;
  const [open, setOpen] = useState(openByDefault);

  const onCategoryCheck = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const mapped = tags.map(tag => {
        const part = pick(tag, ['id', 'name', 'tagCategoryId']);
        return part;
      });
      let action = 'ADD';
      if (!e.target.checked) {
        action = 'REMOVE';
      }
      const withAction = [mapped, action] as FilterWithOperation;
      onTagFilterChange(withAction);
    },
    [tags, onTagFilterChange],
  );

  const onRowClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    if (!(e.target instanceof HTMLInputElement)) setOpen(prev => !prev);
  }, []);

  const defaultCtx: LoadMoreCtx = {
    fetchNextPage: noop,
    hasNextPage: false,
    loadMoreText:
      tags.length === 0 ? 'There are no tags in this category' : undefined,
  };
  const { slice, loadMoreCtx } = useVirtualPages(
    tags,
    undefined,
    undefined,
    defaultCtx,
  );
  return (
    <div {...rest}>
      <div
        className="CategoryRow"
        onClick={onRowClick}
        role="row"
        tabIndex={-1}
        onKeyDown={noop}
      >
        <Checkbox
          indeterminate={hasCategory}
          checked={hasCategory}
          icon={CheckIcon}
          onChange={onCategoryCheck}
        />
        {open ? <ArrowUpImg /> : <ChevronDown />}
        <div className="CategoryrowTitle">
          <div className="IconCircle" />
          <div className="H400">{category.name}</div>
        </div>
      </div>
      {open &&
        slice.map(tag => {
          const hasTag = !!categorySelected?.has(tag.id);
          return (
            <div className="CategoryRow_Tag" key={tag.id}>
              <TagCheckbox
                hasTag={hasTag}
                tag={tag}
                onTagFilterChange={onTagFilterChange}
              />
              <Tag
                disableCategoryTip
                tagName={tag.name}
                id={tag.id}
                color={category.color}
              />
            </div>
          );
        })}
      <LoadMoreContext.Provider value={loadMoreCtx}>
        {open && <LoadMoreComponent />}
      </LoadMoreContext.Provider>
    </div>
  );
}
